import { ButtonWrapper } from "@insite/mobius/Button";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import breakpointUtility from "@store/Framework/Common/Utilities/breakpoint";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import FlexWrapper, { FlexWrapperProps, FlexWrapperStyles } from "@store/Library/Components/Toolkit/FlexWrapper";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import React, { FC, useMemo } from "react";
import { css, useTheme } from "styled-components";

export interface FormActionsProps extends ExpandStyles<FormActionsStyles> {
    fullWidth?: boolean;
    gap?: FlexWrapperProps["gap"];
    verticalGap?: FlexWrapperProps["verticalGap"];
    spacing?: string;
    breakpoint?: BreakpointKey;
    children?: React.ReactNode;
}

export interface FormActionsStyles {
    flexWrapper?: FlexWrapperStyles;
}

export const formActionsStyles: FormActionsStyles = {
    flexWrapper: {
        item: {
            css: css`
                width: 100%;

                ${ButtonWrapper},
                > * {
                    width: 100%;
                }
            `,
        },
    },
};

const FormActions: FC<FormActionsProps> = ({ children, fullWidth, extendedStyles, overridedStyles, ...props }) => {
    const baseStyles = useExpandProps(formActionsStyles, extendedStyles, overridedStyles);
    const theme = useTheme();

    const breakpoint = props.breakpoint || "sm";
    const gap = props.gap || theme.design.form.actions.gap;
    const verticalGap = props.verticalGap || theme.design.form.actions.verticalGap;
    const spacing = props.spacing || theme.design.form.actions.spacing || null;

    const styles = useMemo(() => {
        return mergeProps<FormActionsStyles>(
            {
                flexWrapper: {
                    wrapper: {
                        css: spacingStyles(spacing, null),
                    },
                    item: {
                        css: css`
                            ${() => {
                                if (!fullWidth) {
                                    return css`
                                        ${breakpointUtility(breakpoint)} {
                                            width: auto;
                                        }
                                    `;
                                }
                            }}
                        `,
                    },
                },
            },
            baseStyles,
        );
    }, [fullWidth, breakpoint, spacing, baseStyles]);

    if (!children) {
        return null;
    }

    return (
        <FlexWrapper gap={gap} verticalGap={verticalGap} extendedStyles={styles.flexWrapper}>
            {children}
        </FlexWrapper>
    );
};

export default FormActions;
